import { Helmet } from 'react-helmet-async'
import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { db } from '../lib/supabase'
import LoadingSpinner from '../components/LoadingSpinner'

export default function Tiendas() {
  const [searchParams] = useSearchParams()
  const [tiendas, setTiendas] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showOnlyDiscounts, setShowOnlyDiscounts] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('')
  const [heroConfig, setHeroConfig] = useState(null)
  const [seoConfig, setSeoConfig] = useState(null)
  const [faqs, setFaqs] = useState([])
  const [categorias, setCategorias] = useState([])

  useEffect(() => {
    fetchTiendas()
    fetchCategorias()
    loadHeroConfig()
    loadSEOConfig()
    loadFAQs()
  }, [])

  useEffect(() => {
    const hasDescuentosParam = searchParams.get('descuentos') === 'true'
    setShowOnlyDiscounts(hasDescuentosParam)
    
    const searchQuery = searchParams.get('q')
    if (searchQuery) {
      setSearchTerm(searchQuery)
    }
  }, [searchParams])

  const fetchTiendas = async () => {
    try {
      setLoading(true)
      const { data, error } = await db.getTiendas()
      if (error) throw error
      setTiendas(data || [])
    } catch (error) {
      console.error('Error fetching tiendas:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchCategorias = async () => {
    try {
      const { data } = await db.getCategorias('Locatario')
      setCategorias(data || [])
    } catch (error) {
      console.error('Error fetching categorias:', error)
    }
  }

  const loadHeroConfig = async () => {
    const { data } = await db.getConfiguracionPortada('tiendas')
    setHeroConfig(data || null)
  }

  const loadSEOConfig = async () => {
    const { data } = await db.getSEOConfig('tiendas')
    setSeoConfig(data || null)
  }

  const loadFAQs = async () => {
    const { data } = await db.getFAQsByScope('page', null, 'tiendas')
    setFaqs(data || [])
  }

  const filteredTiendas = tiendas.filter(tienda => {
    const matchesSearch = tienda.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = !selectedCategory || tienda.categoria_id === selectedCategory
    const hasDescuentos = tienda.descuentos && tienda.descuentos[0]?.count > 0
    const matchesDiscount = !showOnlyDiscounts || hasDescuentos
    return matchesSearch && matchesCategory && matchesDiscount
  })

  if (loading) return <LoadingSpinner />

  return (
    <>
      <Helmet>
        <title>{seoConfig?.meta_title || 'Tiendas - Plaza Aurora'}</title>
        <meta name="description" content={seoConfig?.meta_description || 'Descubre las mejores tiendas y marcas en Plaza Aurora Outlet. Encuentra moda, tecnología, deportes y más.'} />
        <link rel="canonical" href={seoConfig?.canonical_url || `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/tiendas2`} />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="es_PE" />
        <meta property="og:site_name" content="Plaza Aurora" />
        <meta property="og:url" content={seoConfig?.canonical_url || `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/tiendas2`} />
        <meta property="og:title" content={seoConfig?.og_title || seoConfig?.meta_title || 'Tiendas - Plaza Aurora'} />
        <meta property="og:description" content={seoConfig?.og_description || seoConfig?.meta_description || 'Descubre las mejores tiendas y marcas en Plaza Aurora Outlet.'} />
        {seoConfig?.og_image_url && <meta property="og:image" content={seoConfig.og_image_url} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content={seoConfig?.twitter_card || 'summary_large_image'} />
        <meta name="twitter:title" content={seoConfig?.og_title || seoConfig?.meta_title || 'Tiendas - Plaza Aurora'} />
        <meta name="twitter:description" content={seoConfig?.og_description || seoConfig?.meta_description || 'Descubre las mejores tiendas y marcas en Plaza Aurora Outlet.'} />
        {seoConfig?.og_image_url && <meta name="twitter:image" content={seoConfig.og_image_url} />}
        
        {/* Robots */}
        <meta name="robots" content={seoConfig?.robots_index === false ? 'noindex,nofollow' : 'index,follow'} />
      </Helmet>

      {/* Breadcrumb JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Inicio",
              "item": `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/`
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Tiendas",
              "item": `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/tiendas2`
            }
          ]
        })}
      </script>
      
      {seoConfig?.json_ld && (
        <script type="application/ld+json">
          {seoConfig.json_ld}
        </script>
      )}

      <main>
        <div className="container" style={{
          maxWidth: 'var(--container-max-width)',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          {/* Hero Section */}
          <section id="hero_main" role="region" aria-label="Hero Tiendas" style={{ marginTop: '1.5rem' }}>
            <div className="heroContainer" style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth >= 768 ? '1fr 0.9fr' : '1fr',
              alignItems: 'center',
              gap: '1.5rem',
              padding: '1.25rem',
              background: 'var(--color-accent-gray)',
              borderRadius: 'var(--radius-lg)',
              overflow: 'hidden'
            }}>
              <div className="heroLeft" style={{ padding: '1rem' }}>
                <h1 className="heroTitle" style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 'var(--font-weight-bold)',
                  fontSize: window.innerWidth >= 768 ? 'var(--font-size-h1)' : 'var(--font-size-h2)',
                  lineHeight: '0.95',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 1rem 0'
                }}>
                  {heroConfig?.hero_titulo || 'LAS MEJORES TIENDAS EN LIMA NORTE'}
                </h1>
                <p className="heroDescription" style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-lead)',
                  color: 'var(--color-text-muted)',
                  maxWidth: '48ch',
                  lineHeight: 'var(--line-height-relaxed)',
                  marginBottom: '1.5rem'
                }}>
                  {heroConfig?.hero_descripcion || 'Encuentra las mejores marcas y ofertas en un solo lugar 🛍️'}
                </p>
                {heroConfig?.hero_boton_1_texto && (
                  <div className="heroActions" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <Link 
                      to={heroConfig?.hero_boton_1_url || '/tiendas2'} 
                      style={{
                        padding: '0.6rem 1.1rem',
                        background: 'var(--color-cta-bg)',
                        color: 'var(--color-cta-text)',
                        borderRadius: 'var(--radius-sm)',
                        fontWeight: 'var(--font-weight-bold)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontSize: 'var(--font-size-body)',
                        textDecoration: 'none',
                        display: 'inline-block',
                        border: heroConfig?.hero_boton_1_estilo === 'outline' ? '2px solid var(--color-cta-bg)' : 'none'
                      }}
                    >
                      {heroConfig.hero_boton_1_texto}
                    </Link>
                    {heroConfig?.hero_boton_2_texto && (
                      <Link 
                        to={heroConfig?.hero_boton_2_url || '#'} 
                        style={{
                          padding: '0.6rem 1.1rem',
                          background: heroConfig?.hero_boton_2_estilo === 'outline' ? 'transparent' : 'var(--color-cta-bg)',
                          color: heroConfig?.hero_boton_2_estilo === 'outline' ? 'var(--color-text-primary)' : 'var(--color-cta-text)',
                          border: heroConfig?.hero_boton_2_estilo === 'outline' ? '2px solid var(--color-stroke)' : 'none',
                          borderRadius: 'var(--radius-sm)',
                          fontWeight: 'var(--font-weight-bold)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          fontSize: 'var(--font-size-body)',
                          textDecoration: 'none',
                          display: 'inline-block'
                        }}
                      >
                        {heroConfig.hero_boton_2_texto}
                      </Link>
                    )}
                  </div>
                )}
              </div>
              {heroConfig?.hero_imagen_url && (
                <div className="heroRight" style={{ 
                  display: window.innerWidth >= 768 ? 'block' : 'none'
                }}>
                  <img 
                    src={heroConfig.hero_imagen_url} 
                    alt={heroConfig.hero_imagen_alt || 'Tiendas Plaza Aurora'} 
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: 'var(--radius-md)',
                      objectFit: 'cover'
                    }}
                  />
                </div>
              )}
            </div>
          </section>

          {/* Marquee Bar */}
          {heroConfig?.marquee_activo && heroConfig?.marquee_texto && (
            <div style={{
              background: '#000000',
              color: '#FFFFFF',
              padding: '0.75rem 0',
              marginTop: '1.5rem',
              overflow: 'hidden',
              borderRadius: 'var(--radius-sm)',
              cursor: heroConfig?.marquee_link_url ? 'pointer' : 'default',
              transition: 'opacity 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (heroConfig?.marquee_link_url) {
                e.currentTarget.style.opacity = '0.8'
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1'
            }}
            onClick={() => {
              if (heroConfig?.marquee_link_url) {
                if (heroConfig.marquee_link_url.startsWith('/')) {
                  window.location.href = heroConfig.marquee_link_url
                } else {
                  window.open(heroConfig.marquee_link_url, '_blank', 'noopener,noreferrer')
                }
              }
            }}
            >
              <div style={{
                display: 'flex',
                whiteSpace: 'nowrap',
                animation: 'scroll-left 20s linear infinite'
              }}>
                <span style={{ 
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-body)',
                  fontWeight: 'var(--font-weight-bold)',
                  padding: '0 2rem'
                }}>
                  {heroConfig.marquee_texto}
                </span>
                <span style={{ 
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-body)',
                  fontWeight: 'var(--font-weight-bold)',
                  padding: '0 2rem'
                }}>
                  {heroConfig.marquee_texto}
                </span>
              </div>
            </div>
          )}

          {/* Search and Filter */}
          <section style={{ marginTop: '2rem' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth >= 768 ? '2fr 1fr 1fr' : '1fr',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <input
                type="text"
                placeholder="Buscar tienda..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  padding: '0.75rem 1rem',
                  border: '1px solid var(--color-stroke)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-body)',
                  fontFamily: 'var(--font-ui)'
                }}
              />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{
                  padding: '0.75rem 1rem',
                  border: '1px solid var(--color-stroke)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-body)',
                  fontFamily: 'var(--font-ui)'
                }}
              >
                <option value="">Todas las categorías</option>
                {categorias.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                ))}
              </select>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                border: '1px solid var(--color-stroke)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                background: showOnlyDiscounts ? 'var(--color-accent-gray)' : 'white'
              }}>
                <input
                  type="checkbox"
                  checked={showOnlyDiscounts}
                  onChange={(e) => setShowOnlyDiscounts(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: 'var(--font-size-body)' }}>Solo con descuentos</span>
              </label>
            </div>
          </section>

          {/* Tiendas Grid */}
          <section>
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth >= 1024 ? 'repeat(4, 1fr)' : window.innerWidth >= 768 ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
              gap: '1.5rem'
            }}>
              {filteredTiendas.map(tienda => (
                <Link
                  key={tienda.id}
                  to={`/tiendas2/${tienda.id}`}
                  style={{
                    background: 'var(--color-surface)',
                    borderRadius: 'var(--radius-lg)',
                    overflow: 'hidden',
                    border: '1px solid var(--color-stroke)',
                    transition: 'all 0.2s ease',
                    textDecoration: 'none',
                    display: 'block'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)'
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                >
                  <div style={{
                    width: '100%',
                    paddingTop: '100%',
                    position: 'relative',
                    background: 'var(--color-accent-gray)'
                  }}>
                    {tienda.logo_url && (
                      <img
                        src={tienda.logo_url}
                        alt={tienda.nombre}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          padding: '1rem'
                        }}
                      />
                    )}
                  </div>
                  <div style={{ padding: '1rem' }}>
                    <h3 style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-body)',
                      fontWeight: 'var(--font-weight-bold)',
                      color: 'var(--color-text-primary)',
                      margin: '0 0 0.5rem 0'
                    }}>
                      {tienda.nombre}
                    </h3>
                    {tienda.categoria && (
                      <p style={{
                        fontSize: 'var(--font-size-small)',
                        color: 'var(--color-text-muted)',
                        margin: 0
                      }}>
                        {tienda.categoria.nombre}
                      </p>
                    )}
                    {tienda.descuentos && tienda.descuentos[0]?.count > 0 && (
                      <div style={{
                        marginTop: '0.5rem',
                        padding: '0.25rem 0.5rem',
                        background: 'var(--color-cta-bg)',
                        color: 'white',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-small)',
                        fontWeight: 'var(--font-weight-bold)',
                        display: 'inline-block'
                      }}>
                        🏷️ {tienda.descuentos[0].count} descuento{tienda.descuentos[0].count !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>

            {filteredTiendas.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '3rem 1rem',
                color: 'var(--color-text-muted)'
              }}>
                <p style={{ fontSize: 'var(--font-size-lead)' }}>
                  No se encontraron tiendas con los filtros seleccionados
                </p>
              </div>
            )}
          </section>

          {/* FAQs Section */}
          {faqs.length > 0 && (
            <section style={{
              marginTop: '3rem',
              maxWidth: '1200px',
              margin: '3rem auto 0',
              padding: '0 1rem'
            }}>
              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--font-size-h2)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--color-text-primary)',
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}>
                Preguntas Frecuentes
              </h2>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                {faqs.map(faq => (
                  <details
                    key={faq.id}
                    style={{
                      background: 'var(--color-surface)',
                      border: '1px solid var(--color-stroke)',
                      borderRadius: 'var(--radius-md)',
                      padding: '1rem'
                    }}
                  >
                    <summary style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-body)',
                      fontWeight: 'var(--font-weight-bold)',
                      color: 'var(--color-text-primary)',
                      cursor: 'pointer',
                      listStyle: 'none'
                    }}>
                      {faq.pregunta}
                    </summary>
                    <p style={{
                      marginTop: '0.75rem',
                      fontSize: 'var(--font-size-body)',
                      color: 'var(--color-text-muted)',
                      lineHeight: 'var(--line-height-relaxed)'
                    }}>
                      {faq.respuesta}
                    </p>
                  </details>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  )
}
